<template>
  <div :class="classes">
    <label v-if="labelAbove" :for="$attrs.id" class="multiselect__label-above">
      {{ labelAbove }}
    </label>
    <div
      :class="selectFieldClasses"
      @click="openMenu"
      v-click-outside="onClickOutside"
      tabindex="0"
      @keydown="onKeyPress"
    >
      <template v-for="(selectedOption, index) in getSelectedOptions()">
        <m4-tag
          v-if="selectedOption"
          :key="index"
          :title="strippedHtml(selectedOption.label)"
          @onRemoveClick="removeItem(selectedOption.value)"
        >
          <input v-if="!valuesToString" type="hidden" :name="name + '[]'" :value="selectedOption.value" />
        </m4-tag>
      </template>
      <input
        v-if="valuesToString && getSelectedOptions().length"
        type="hidden"
        :name="name"
        :value="selected.join(this.valuesToStringSeparator)"
      />
      <input v-if="getSelectedOptions().length === 0" type="hidden" :name="name" />
    </div>
    <label v-if="$attrs.placeholder" ref="floatingPlaceholder" :for="$attrs.id" :class="floatingPlaceholderClasses">
      {{ $attrs.placeholder }}
    </label>
    <a class="multiselect__icon__wrapper" @click="closeOpenMenu">
      <span :class="selectIconClasses"></span>
    </a>
    <div v-if="selectMenuOpen" class="multiselect__list__wrapper">
      <input
        v-if="withSearchField"
        type="text"
        class="multiselect__list__search-field"
        ref="search"
        @keyup="onSearchFromOptions"
        autocomplete="no"
        placeholder="Start typing to search"
        :value="searchPhrase"
      />
      <div v-if="searchPhrase.length > 0 && currOptions.length === 0" class="multiselect__list__no-result">
        No results found
      </div>
      <ul class="multiselect__list">
        <li
          v-for="option in currOptions"
          @click="option.group ? () => {} : selectItem(option.value, option)"
          :class="optionClasses(option)"
          :key="option.value"
        >
          <div
            v-if="option.group"
            :class="
              'multiselect__list__group-item__options' +
              (!defaultOpenedGroups ? ' multiselect__list__group-item__options--expandable' : '') +
              (option.opened ? ' multiselect__list__group-item__options--opened' : '')
            "
          >
            <div @click="selectItem(option.value, option)" class="multiselect__list__group-item__options__label">
              <span class="multiselect__list__group-item__options__label__text">
                {{ option.group }}
                <m4-icon class="multiselect__list__group-item__options__label__text" icon="angle-down"></m4-icon>
              </span>
              <small
                v-if="!defaultOpenedGroups && !option.opened"
                class="multiselect__list__group-item__options__label__text multiselect__list__item__note"
              >
                Click to expand
              </small>
              <small
                v-if="!defaultOpenedGroups && option.opened"
                class="multiselect__list__group-item__options__label__text multiselect__list__item__note"
              >
                Click to collapse
              </small>
            </div>
            <ul class="multiselect__list__group-item__options__list">
              <li
                v-for="groupOption in option.options"
                @click="selectItem(groupOption.value, groupOption)"
                :class="optionClasses(groupOption)"
                :key="groupOption.value"
              >
                <span class="multiselect__list__item__title" v-if="groupOption.label">
                  {{ strippedHtml(groupOption.label) }}
                  <m4-icon v-if="isGroupValues(groupOption)" icon="list-alt"></m4-icon>
                </span>
                <small class="multiselect__list__item__note" v-if="isSelectedOption(groupOption)">
                  <span>Selected</span>
                  <span>Click to remove</span>
                </small>
                <small class="multiselect__list__item__note" v-else>Click to select</small>
              </li>
            </ul>
          </div>
          <template v-else>
            <span class="multiselect__list__item__title" v-if="option.label">
              {{ strippedHtml(option.label) }}
              <m4-icon v-if="isGroupValues(option)" icon="list-alt"></m4-icon>
            </span>
            <small class="multiselect__list__item__note" v-if="isSelectedOption(option)">
              <span>Selected</span>
              <span>Click to remove</span>
            </small>
            <small class="multiselect__list__item__note" v-else>Click to select</small>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import ClickOutside from "click-outside-vue3";

export default {
  name: "M4Multiselect",
  data() {
    let selected = this.modelValue && this.modelValue.length > 0 ? this.modelValue : this.value;
    if (typeof selected === "string") {
      selected = selected.split(this.valuesToStringSeparator);
    }
    if (selected === null) {
      selected = [];
    }

    let currOptions = Object.assign([], this.options);
    currOptions = currOptions.map((option) => {
      option.opened = this.defaultOpenedGroups;
      return option;
    });

    let initOptions = Object.assign([], this.options);

    return {
      selected: selected,
      selectMenuOpen: false,
      searchPhrase: "",
      searchIntv: null,
      currOptions: currOptions,
      initOptions: initOptions,
      floatingPlaceholderRows: 1
    };
  },
  props: {
    error: {
      type: Boolean,
      default: false
    },
    name: {
      type: String,
      required: true
    },
    value: {
      type: [Array, String],
      default() {
        return [];
      }
    },
    modelValue: {
      type: [Array, String],
      default() {
        return [];
      }
    },
    options: {
      type: Array,
      default() {
        return [];
      }
    },
    valuesToString: {
      type: Boolean,
      default: false
    },
    valuesToStringSeparator: {
      type: String,
      default() {
        return "||";
      }
    },
    labelAbove: String,
    withSearchField: {
      type: Boolean,
      default: false
    },
    defaultOpenedGroups: {
      type: Boolean,
      default: true
    }
  },
  emits: ["update:modelValue", "click"],
  mounted() {
    if (this.$refs.floatingPlaceholder && this.$refs.floatingPlaceholder.offsetHeight >= 14) {
      this.floatingPlaceholderRows = Math.floor(this.$refs.floatingPlaceholder.offsetHeight / 11);
    }
  },
  computed: {
    classes() {
      let classes = ["multiselect"];
      if (this.$attrs.placeholder) {
        classes.push("multiselect--floating-placeholder");
      }
      if (this.isDisabled) {
        classes.push("multiselect--disabled");
      }
      if (this.selected.length > 0) {
        classes.push("multiselect--has-selected-option");
      }
      if (this.selectMenuOpen) {
        classes.push("multiselect--opened-menu");
      }
      classes.push("multiselect--floating-placeholder-rows-" + this.floatingPlaceholderRows);

      return classes;
    },
    selectFieldClasses() {
      let classes = ["multiselect__field"];
      if (this.error) {
        classes.push("multiselect__field--error");
      }
      if (this.selected) {
        classes.push("multiselect__field--selected");
      }
      return classes;
    },
    selectIconClasses() {
      let classes = ["multiselect__icon"];
      if (this.selectMenuOpen) {
        classes.push("multiselect__icon--open");
      }
      return classes;
    },
    floatingPlaceholderClasses() {
      let classes = ["multiselect__floating-placeholder"];
      if (this.error) {
        classes.push("multiselect__floating-placeholder--error");
      }
      return classes;
    },
    isDisabled() {
      return this.$attrs.disabled !== undefined;
    },
    computedValue: {
      get() {
        return this.selected;
      },
      set(value) {
        this.selected = value;
        this.$emit("update:modelValue", value);
      }
    }
  },
  methods: {
    clearSearchData() {
      if (this.withSearchField) {
        this.searchPhrase = "";
        this.currOptions = this.initOptions;
      }
    },
    addFocusToSearch() {
      setTimeout(() => {
        if (this.$refs.search) {
          this.$refs.search.focus();
        }
      }, 50);
    },
    onSearchFromOptions(e) {
      let filteredOptions = [];

      if (this.searchIntv) {
        clearTimeout(this.searchIntv);
      }
      this.searchIntv = setTimeout(() => {
        this.searchPhrase = e.target.value;

        if (this.searchPhrase.trim() === "") {
          return this.clearSearchData();
        }

        this.initOptions.map((option) => {
          if (this.isGroupOption(option)) {
            let mainGroup = Object.assign({}, option, { options: [] });
            option.options.map((groupOption) => {
              let label = groupOption.label.toLowerCase();
              let index = this.searchPhrase ? label.indexOf(this.searchPhrase.toLowerCase()) : 0;
              if (index > -1) {
                mainGroup.opened = true;
                mainGroup.options.push(groupOption);
              }
            });
            if (mainGroup.options.length > 0) {
              filteredOptions.push(mainGroup);
            }
          } else {
            let label = option.label.toLowerCase();
            let index = this.searchPhrase ? label.indexOf(this.searchPhrase.toLowerCase()) : 0;
            if (index > -1) {
              filteredOptions.push(option);
            }
          }
        });
        this.currOptions = filteredOptions;
      }, 250);
    },
    onKeyPress(e) {
      if (e.which == 40) {
        this.openMenu(e);
      } else if (e.which == 9 || e.which == 27) {
        this.selectMenuOpen = false;
      }
    },
    openMenu(e) {
      if (this.canOpenMenu(e)) {
        this.selectMenuOpen = true;
        if (this.withSearchField) {
          this.addFocusToSearch();
        }
        this.$emit("click", e);
      }
    },
    closeOpenMenu(e) {
      if (this.canOpenMenu(e)) {
        this.selectMenuOpen = !this.selectMenuOpen;
        if (this.withSearchField && this.selectMenuOpen) {
          this.addFocusToSearch();
        }
        this.$emit("click", e);
      }
    },
    toggleGroup(optGroup) {
      this.currOptions = this.currOptions.map((option) => {
        if (option.group && option.group === optGroup.group) {
          option.opened = !option.opened;
        }
        return option;
      });
    },
    canOpenMenu(e) {
      return (
        !e.target.classList.contains("multiselect__field__tag__remove") &&
        e.target.tagName != "rect" &&
        e.target.tagName != "svg"
      );
    },
    isSelected(val) {
      let index = this.selected.indexOf(val);
      if (index === -1) {
        index = this.selected.indexOf(val + "");
      }
      return {
        validation: index > -1,
        index: index
      };
    },
    selectItem(val, option) {
      const isSelected = this.isSelected(val);
      const isGroupValues = this.isGroupValues(option);
      const isGroupOption = this.isGroupOption(option);
      if (isGroupValues) {
        option.values.map((selectOptionVal) => {
          let isOptValSelected = this.isSelected(selectOptionVal);
          if (isOptValSelected.validation) {
            // this.removeItem(selectOptionVal);
          } else {
            this.selected.push(selectOptionVal);
          }
        });
      } else if (isGroupOption) {
        if (!this.defaultOpenedGroups) {
          this.toggleGroup(option);
        }
        return null;
      } else {
        if (isSelected.validation) {
          this.removeItem(val);
        } else {
          this.selected.push(val);
        }
      }

      this.selectMenuOpen = false;
      this.clearSearchData();
      this.$emit("update:modelValue", this.selected);
    },
    removeItem(val) {
      const isSelected = this.isSelected(val);
      if (isSelected.validation) {
        this.selected.splice(isSelected.index, 1);
      }
      this.selectMenuOpen = false;
      this.$emit("update:modelValue", this.selected);
    },
    getSelectedOptions() {
      let tempAdaptOptions = [];
      this.options.map((option) => {
        if (this.isGroupOption(option)) {
          option.options.map((groupOption) => {
            tempAdaptOptions[groupOption.value] = groupOption;
          });
        } else {
          tempAdaptOptions[option.value] = option;
        }
      });

      let selectedOptions = [];

      this.selected.map((val) => {
        if (tempAdaptOptions[val]) {
          selectedOptions.push(tempAdaptOptions[val]);
        }
      });

      return selectedOptions.length ? selectedOptions : [];
    },
    optionClasses(option) {
      let classes = ["multiselect__list__item"];

      if (this.isGroupOption(option)) {
        classes = ["multiselect__list__group-item"];
      }
      if (this.isSelectedOption(option)) {
        classes.push("multiselect__list__item--selected");
      }
      if (this.isGroupValues(option)) {
        classes.push("multiselect__list__item--group-values");
      }
      return classes;
    },
    isSelectedOption(option) {
      const isSelected = this.isSelected(option.value);
      return isSelected.validation;
    },
    isGroupValues(option) {
      return option.values && option.values.length > 0;
    },
    isGroupOption(option) {
      return option.group && option.options.length > 0;
    },
    onClickOutside(event) {
      if (
        !event.target ||
        !event.target.classList ||
        !event.target.parentNode ||
        !event.target.parentNode.classList ||
        !event.target.parentNode.parentNode ||
        !event.target.parentNode.parentNode.classList
      ) {
        this.selectMenuOpen = false;
        return null;
      }

      if (
        event.target &&
        !event.target.classList.contains("multiselect__list__item") &&
        !event.target.classList.contains("multiselect__list__item__title") &&
        !event.target.classList.contains("multiselect__list__search-field") &&
        !event.target.classList.contains("multiselect__list__group-item__options__label") &&
        !event.target.classList.contains("multiselect__list__group-item__options__label__text") &&
        !event.target.classList.contains("multiselect__icon__wrapper") &&
        event.target.parentNode &&
        !event.target.parentNode.classList.contains("multiselect__list__group-item__options__label__text") &&
        event.target.parentNode.parentNode &&
        !event.target.parentNode.parentNode.classList.contains("multiselect__list__group-item__options__label__text")
      ) {
        this.selectMenuOpen = false;
      }
    },
    strippedHtml(html) {
      let regex = /(<([^>]+)>)/gi;
      html = html + "";
      return html.replace(regex, "");
    }
  },
  watch: {
    modelValue(value) {
      this.selected = value;
    },
    value(value) {
      this.selected = value;
    }
  },
  directives: {
    clickOutside: ClickOutside.directive
  }
};
</script>
